import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Newspaper } from "lucide-react";
import AdminActions from "@/components/AdminActions";
import { fontClass } from "@/lib/fonts";
import Seo from "@/components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);

  const load = useCallback(() => {
    api.get("/blog").then(r => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen pt-28">
      <Seo title="Blog" description="Guides, insights and practical AI workflows from PromptsHouseBD." path="/blog" />
      <section className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="overline-label mb-4 flex items-center gap-2"><Newspaper size={13} /> Deep Dives</div>
          <h1 className="font-display font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-white">
            The Blog.<br />
            <span className="font-bn-display italic font-normal text-gradient">Understanding, in depth.</span>
          </h1>
          <p className="text-slate-200 mt-6 max-w-2xl leading-relaxed">
            Longer reads on the shifts that actually change how you work — free resources, workflow breakdowns, and the tools quietly winning behind the scenes.
          </p>
          <div className="mt-6"><AdminActions kind="blog" onChange={load} /></div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl divide-y divide-white/[0.07] overflow-hidden">
          {posts.map((b, i) => (
            <Link
              key={b.id}
              to={`/blog/${b.slug}`}
              className="grid grid-cols-12 gap-5 p-5 md:p-7 hover:bg-white/5 transition-colors group items-center"
              data-testid={`blog-row-${b.slug}`}
            >
              <div className="hidden md:block col-span-1 font-display text-3xl font-bold text-slate-200 group-hover:text-orange-400 transition-colors">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div className="col-span-12 md:col-span-3">
                <div className="aspect-[16/10] overflow-hidden rounded-xl border border-white/[0.07]">
                  <img src={b.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="flex items-center gap-3 text-xs text-slate-200 font-medium mb-2">
                  <span>{new Date(b.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  <span>·</span>
                  <span>{b.read_minutes} min read</span>
                </div>
                <h2 className={`${fontClass(b.title_font, "font-bn")} font-bold text-xl md:text-2xl leading-snug text-white group-hover:text-orange-300 transition-colors`}>{b.title}</h2>
                <p className="text-slate-200 mt-2 font-bn text-sm line-clamp-2">{b.excerpt}</p>
              </div>
              <div className="hidden md:flex col-span-2 justify-end items-center gap-2">
                <AdminActions kind="blog" item={b} onChange={load} mode="row" />
                <ArrowUpRight size={24} className="text-slate-200 group-hover:text-orange-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
